/**
 * Global React Query error → toast handlers, wired into the QueryClient in
 * `index.tsx` (`MutationCache.onError` / `QueryCache.onError`).
 *
 * Extracted here (rather than inline in the app entry) so the suppression and
 * dedupe logic is unit-testable without importing `index.tsx`'s side effects
 * (root render, offline bootstrap, OTA, …).
 *
 * Both handlers surface the standard translated message via a `mutation-error`
 * custom event; `NotificationContext` listens and calls `toast.error`. Dispatching
 * an event (rather than importing the notification context) avoids a circular
 * dependency. See `docs/platform/error-handling-design.md`.
 */
import { getErrorMessage, hasErrorCode, isNetworkError, getErrorStatus, getErrorId } from './errors';

/** Dispatch a toast for a failed request via the shared custom-event path. */
export function dispatchErrorToast(message: string): void {
  window.dispatchEvent(new CustomEvent('mutation-error', { detail: { message } }));
}

// In a read-only demo, mutating requests are rejected with DEMO_READ_ONLY by the
// backend guard. User-initiated saves SHOULD explain why, but background writes
// (auto-saves, read-receipts, telemetry) must not spam a toast per request — so
// the demo toast is throttled to at most once every few seconds.
let lastDemoReadOnlyToastAt = 0;
const DEMO_READ_ONLY_TOAST_INTERVAL_MS = 4000;

/**
 * Global mutation error handler. Shows a user-friendly, translated toast for
 * every failed React Query mutation.
 *
 * A mutation may opt out by setting `meta: { suppressGlobalErrorToast: true }` —
 * used when a surface shows its own domain-specific error message (e.g. the
 * fulfilment pick/pack/ship dialogs name the product + warehouse on
 * INSUFFICIENT_STOCK) and a second generic toast would be redundant.
 */
export function handleMutationError(
  error: unknown,
  _variables?: unknown,
  _context?: unknown,
  mutation?: { meta?: Record<string, unknown> },
): void {
  if (mutation?.meta?.suppressGlobalErrorToast) return;

  if (hasErrorCode(error, 'DEMO_READ_ONLY')) {
    const now = Date.now();
    if (now - lastDemoReadOnlyToastAt < DEMO_READ_ONLY_TOAST_INTERVAL_MS) return;
    lastDemoReadOnlyToastAt = now;
  }

  dispatchErrorToast(getErrorMessage(error));
}

// Short window in which a repeated identical query error is toasted only once,
// so a burst of queries failing with the same backend error shows one toast.
const DEDUPE_WINDOW_MS = 3000;
const recentQueryErrorAt = new Map<string, number>();

/**
 * Global read-QUERY error handler — the read-side companion to
 * handleMutationError. QueryCache.onError fires once per query error (not once
 * per subscribing component), so a shared list backed by one query never
 * double-toasts. On top of that we:
 *  - opt out per query via `meta: { suppressGlobalErrorToast: true }`;
 *  - suppress connectivity failures (the OfflineBanner already says "offline");
 *  - suppress 401 (SessionExpiryHandler re-auths) and page-handled 403/404
 *    (permission-gated / missing-entity views render their own state);
 *  - dedupe by error id / message within a short window.
 */
export function handleQueryError(
  error: unknown,
  query?: { meta?: Record<string, unknown> },
): void {
  if (query?.meta?.suppressGlobalErrorToast) return;

  if (isNetworkError(error)) return;

  const status = getErrorStatus(error);
  if (status === 401 || status === 403 || status === 404) return;

  const message = getErrorMessage(error);

  const key = getErrorId(error) ?? message;
  const now = Date.now();
  const lastAt = recentQueryErrorAt.get(key);
  if (lastAt !== undefined && now - lastAt < DEDUPE_WINDOW_MS) return;
  recentQueryErrorAt.set(key, now);
  // Prune stale keys so the map can't grow unbounded.
  for (const [k, at] of recentQueryErrorAt) {
    if (now - at >= DEDUPE_WINDOW_MS) recentQueryErrorAt.delete(k);
  }

  dispatchErrorToast(message);
}
